import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  AdminService,
  FlowerStockBouquetEntry,
  FlowerStockCutFlowerEntry
} from '../services/admin.service';

interface ProductItem {
  key: string;
  label: string;
  apiName: string;
}

interface DailyRow {
  opening: number;
  scrap: number;
  sold: number;
  closing: number;
}

@Component({
  selector: 'app-cut-flower-stock',
  templateUrl: './cut-flower-stock.component.html',
  styleUrls: ['./cut-flower-stock.component.scss']
})
export class CutFlowerStockComponent implements OnInit {
  activeTab: 'bouquets' | 'stock' = 'bouquets';

  bouquetColumns = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];

  products: ProductItem[] = [
    { key: 'voros_rozsa', label: 'Vörös Rózsa', apiName: 'Vörös Rózsa' },
    { key: 'feher_rozsa', label: 'Fehér Rózsa', apiName: 'Fehér Rózsa' },
    { key: 'rozsaszin_rozsa', label: 'Rózsaszín Rózsa', apiName: 'Rózsaszín Rózsa' },
    { key: 'pink_rozsa', label: 'Pink Rózsa', apiName: 'Pink Rózsa' },
    { key: 'narancs_rozsa', label: 'Narancs Rózsa', apiName: 'Narancs Rózsa' },
    { key: 'barack_rozsa', label: 'Barack Rózsa', apiName: 'Barack Rózsa' },
    { key: 'rozsaszin_csoportos_rozsa', label: 'Rózsaszín Csoportos Rózsa', apiName: 'Rózsaszín Csoportos Rózsa' },
    { key: 'puder_rozsa', label: 'Púder Rózsa', apiName: 'Púder Rózsa' },
    { key: 'krem_csoportos_rozsa', label: 'Krém Csoportos Rózsa', apiName: 'Krém Csoportos Rózsa' },
    { key: 'barack_csoportos_rozsa', label: 'Barack Csoportos Rózsa', apiName: 'Barack Csoportos Rózsa' },
    { key: 'pink_csoportos_rozsa', label: 'Pink Csoportos Rózsa', apiName: 'Pink Csoportos Rózsa' },
    { key: 'sarga_rozsa', label: 'Sárga Rózsa', apiName: 'Sárga Rózsa' },
    { key: 'alstroemeria', label: 'Alstroemeria', apiName: 'Alstroemeria' },
    { key: 'lisianthus', label: 'Lisianthus', apiName: 'Lisianthus' },
    { key: 'csoportos_szegfu', label: 'Csoportos szegfű', apiName: 'Csoportos szegfű' },
    { key: 'nagyfeju_szegfu', label: 'Nagyfejű szegfű', apiName: 'Nagyfejű szegfű' },
    { key: 'tulipan', label: 'Tulipán', apiName: 'Tulipán' },
    { key: 'eryngium', label: 'Eryngium', apiName: 'Eryngium' },
    { key: 'jacint', label: 'Jácint', apiName: 'Jácint' },
    { key: 'paeonia', label: 'Paeonia', apiName: 'Paeonia' },
    { key: 'frezia', label: 'Frézia', apiName: 'Frézia' },
    { key: 'vanda_orchidea', label: 'Vanda orchidea', apiName: 'Vanda orchidea' },
    { key: 'krizantem', label: 'Krizantém', apiName: 'Krizantém' },
    { key: 'rezgo', label: 'Rezgő', apiName: 'Rezgő' },
    { key: 'hortenzia', label: 'Hortenzia', apiName: 'Hortenzia' },
    { key: 'boglarka', label: 'Boglárka', apiName: 'Boglárka' },
    { key: 'gerbera', label: 'Gerbera', apiName: 'Gerbera' },
    { key: 'lillium', label: 'Lillium', apiName: 'Lillium' },
    { key: 'szerencsebambusz', label: 'Szerencsebambusz', apiName: 'Szerencsebambusz' },
    { key: 'anthurium', label: 'Anthurium', apiName: 'Anthurium' },
    { key: 'santini', label: 'Santini', apiName: 'Santini' },
    { key: 'wax', label: 'Wax', apiName: 'Wax' },
    { key: 'ananasz', label: 'Ananász', apiName: 'Ananász' },
    { key: 'hellebore', label: 'Helleborus', apiName: 'Helleborus' },
    { key: 'limonium', label: 'Limonium', apiName: 'Limonium' },
    { key: 'ruscus', label: 'Ruscus', apiName: 'Ruscus' },
    { key: 'eucaliptus', label: 'Eucaliptus', apiName: 'Eucaliptus' },
    { key: 'aralia', label: 'Arália', apiName: 'Arália' },
    { key: 'rumora', label: 'Rumora', apiName: 'Rumora' },
    { key: 'aspi', label: 'Aspi', apiName: 'Aspi' },
    { key: 'hukk', label: 'Hukk', apiName: 'Hukk' }
  ];

  bouquetData: Record<string, Record<number, number>> = {};
  bouquetMessage: string | null = null;

  selectedDate = this.toDateInputValue(new Date());
  dailyData: Record<string, DailyRow> = {};
  dailyMessage: string | null = null;
  loadingDaily = false;
  loadingBouquets = false;

  constructor(private adminService: AdminService) {}

  ngOnInit(): void {
    this.initBouquetData();
    this.initDailyData();
    this.loadBouquets(this.selectedDate);
    this.loadDaily(this.selectedDate);
  }

  setTab(tab: 'bouquets' | 'stock') {
    this.activeTab = tab;
  }

  onDateChange(dateValue: string) {
    this.selectedDate = dateValue;
    this.loadBouquets(dateValue);
    this.loadDaily(dateValue);
  }

  async loadBouquets(dateValue: string) {
    this.loadingBouquets = true;
    this.bouquetMessage = null;

    try {
      const response = await firstValueFrom(this.adminService.getFlowerStock(dateValue));
      this.applyBouquetEntries(response?.bouquets ?? []);
    } catch (err) {
      console.error(err);
      this.bouquetMessage = 'Nem sikerült betölteni a csokor táblázatot.';
    } finally {
      this.loadingBouquets = false;
    }
  }

  async saveBouquets() {
    this.bouquetMessage = null;

    try {
      const bouquets = this.buildBouquetPayload();
      await firstValueFrom(
        this.adminService.saveFlowerStockBouquets(this.selectedDate, {
          date: this.selectedDate,
          bouquets
        })
      );

      this.bouquetMessage = 'Csokor adatok elmentve.';
    } catch (err) {
      console.error(err);
      this.bouquetMessage = 'Mentés sikertelen.';
    }
  }

  async loadDaily(dateValue: string) {
    this.loadingDaily = true;
    this.dailyMessage = null;

    try {
      const currentResponse = await firstValueFrom(this.adminService.getFlowerStock(dateValue));
      const currentItems = currentResponse?.cutFlowers ?? [];
      const previousDate = this.shiftDate(dateValue, -1);
      const previousResponse = await firstValueFrom(this.adminService.getFlowerStock(previousDate));
      const previousItems = previousResponse?.cutFlowers ?? [];

      const currentMap = this.mapCutFlowerEntries(currentItems);
      const previousMap = this.mapCutFlowerEntries(previousItems);
      const daily: Record<string, DailyRow> = {};

      this.products.forEach((product) => {
        const existing = currentMap[product.apiName];
        const previous = previousMap[product.apiName];
        const opening = existing?.opening ?? previous?.closing ?? 0;
        const scrap = existing?.scrap ?? 0;
        const sold = existing?.sold ?? 0;
        const closing = existing?.closing ?? this.computeClosing(opening, scrap, sold);

        daily[product.key] = { opening, scrap, sold, closing };
      });

      this.dailyData = daily;
    } catch (err) {
      console.error(err);
      this.dailyMessage = 'Nem sikerült betölteni a napi készletet.';
    } finally {
      this.loadingDaily = false;
    }
  }

  async saveDaily() {
    this.dailyMessage = null;

    try {
      this.products.forEach((product) => {
        const row = this.dailyData[product.key];
        row.closing = this.computeClosing(row.opening, row.scrap, row.sold);
      });

      const items = this.buildDailyPayload();
      const response = await firstValueFrom(
        this.adminService.saveFlowerStockCutFlowers(this.selectedDate, {
          date: this.selectedDate,
          items
        })
      );

      if (response?.cutFlowers) {
        this.applyDailyEntries(response.cutFlowers, this.selectedDate);
      }

      this.dailyMessage = 'Napi készlet elmentve.';
    } catch (err) {
      console.error(err);
      this.dailyMessage = 'Mentés sikertelen.';
    }
  }

  updateDailyRow(productKey: string) {
    const row = this.dailyData[productKey];
    if (!row) return;
    row.closing = this.computeClosing(row.opening, row.scrap, row.sold);
  }

  private initBouquetData() {
    this.products.forEach((product) => {
      this.bouquetData[product.key] = {};
      this.bouquetColumns.forEach((col) => {
        this.bouquetData[product.key][col] = 0;
      });
    });
  }

  private initDailyData() {
    this.products.forEach((product) => {
      this.dailyData[product.key] = { opening: 0, scrap: 0, sold: 0, closing: 0 };
    });
  }

  private applyBouquetEntries(entries: FlowerStockBouquetEntry[]) {
    this.initBouquetData();

    entries.forEach((entry) => {
      const index = entry.index;
      if (!this.bouquetColumns.includes(index)) return;

      Object.entries(entry.items ?? {}).forEach(([productName, value]) => {
        const product = this.products.find((item) => item.apiName === productName);
        if (!product) return;
        this.bouquetData[product.key][index] = Number(value ?? 0);
      });
    });
  }

  private applyDailyEntries(entries: FlowerStockCutFlowerEntry[], dateValue: string) {
    const currentMap = this.mapCutFlowerEntries(entries);
    const daily: Record<string, DailyRow> = {};

    this.products.forEach((product) => {
      const existing = currentMap[product.apiName];
      const opening = existing?.opening ?? 0;
      const scrap = existing?.scrap ?? 0;
      const sold = existing?.sold ?? 0;
      const closing = existing?.closing ?? this.computeClosing(opening, scrap, sold);

      daily[product.key] = { opening, scrap, sold, closing };
    });

    this.dailyData = daily;
    this.selectedDate = dateValue;
  }

  private mapCutFlowerEntries(entries: FlowerStockCutFlowerEntry[]) {
    const mapped: Record<string, FlowerStockCutFlowerEntry> = {};
    entries.forEach((entry) => {
      mapped[entry.product] = entry;
    });
    return mapped;
  }

  private buildBouquetPayload(): FlowerStockBouquetEntry[] {
    const bouquets: FlowerStockBouquetEntry[] = [];

    this.bouquetColumns.forEach((index) => {
      const items: Record<string, number> = {};
      this.products.forEach((product) => {
        const value = Number(this.bouquetData[product.key][index] ?? 0);
        if (value > 0) {
          items[product.apiName] = value;
        }
      });

      if (Object.keys(items).length > 0) {
        bouquets.push({ index, items });
      }
    });

    return bouquets;
  }

  private buildDailyPayload(): FlowerStockCutFlowerEntry[] {
    return this.products.map((product) => {
      const row = this.dailyData[product.key];
      return {
        product: product.apiName,
        opening: Number(row.opening ?? 0),
        scrap: Number(row.scrap ?? 0),
        sold: Number(row.sold ?? 0)
      };
    });
  }

  private computeClosing(opening: number, scrap: number, sold: number): number {
    return Math.max(Number(opening) - Number(scrap) - Number(sold), 0);
  }

  private shiftDate(dateValue: string, days: number): string {
    const current = new Date(`${dateValue}T00:00:00`);
    current.setDate(current.getDate() + days);
    return this.toDateInputValue(current);
  }

  private toDateInputValue(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
